import { type UseQueryResult, queryOptions, useQueries } from "@tanstack/react-query";

import type {
    AdIntelligenceEnumsPlacementType,
    AdIntelligenceEnumsScreenshotFilter,
    AdIntelligenceEnumsVerificationDevice,
    AdIntelligencePublisherVerificationDetailsRequest,
    AdIntelligenceRecordsDetailedVerification,
    AdIntelligenceRecordsPublisherVerificationDetailsResponse,
} from "@similarweb/pro-api-client";

import { apiClient } from "app/modules/api-client/apiClient";

import { useDisplayAdvertiserPublisherDataAndParams } from "../../hooks/useDisplayAdvertiserPublisherDataAndParams";

export const VERIFICATION_TAB_KEYS: AdIntelligenceEnumsScreenshotFilter[] = ["All", "HasScreenshot", "NoScreenshot"];

interface IUseVerificationDetailsArgs {
    advertiser: string;
    publisher: string;
    dateFrom: string;
    dateTo: string;
    page: number;
    pageSize: number;
    activeTab: AdIntelligenceEnumsScreenshotFilter;
    position?: AdIntelligenceEnumsPlacementType[] | null;
    device?: AdIntelligenceEnumsVerificationDevice;
    enabled?: boolean;
}

type DetailsQueryResult = UseQueryResult<AdIntelligenceRecordsPublisherVerificationDetailsResponse>;

export interface IUseVerificationDetailsResult {
    records: Record<AdIntelligenceEnumsScreenshotFilter, AdIntelligenceRecordsDetailedVerification[]>;
    totalCounts: Record<AdIntelligenceEnumsScreenshotFilter, number>;
    isFetching: boolean;
    isError: boolean;
}

export const useVerificationDetails = ({
    advertiser,
    publisher,
    dateFrom,
    dateTo,
    page,
    pageSize,
    activeTab,
    position,
    device = "All",
    enabled = true,
}: IUseVerificationDetailsArgs): IUseVerificationDetailsResult => {
    const params = useDisplayAdvertiserPublisherDataAndParams();

    const queries = VERIFICATION_TAB_KEYS.map((tab) => {
        // All tabs fetch page=1 with the real pageSize on initial load so tab switching is
        // instant (data already cached). Only the active tab follows the current page — inactive
        // tabs stay pinned to page=1, so they (a) never go out of scope on pagination and
        // (b) don't refetch when the active tab paginates.
        const isActive = tab === activeTab;
        const tabPage = isActive ? page : 1;

        const body = {
            advertiser,
            country: params.country,
            campaignName: params.dataFilters?.campaigns as string[] | undefined,
            dateFrom,
            dateTo,
            device,
            page: tabPage,
            pageSize,
            position,
            publisher,
            screenshotFilter: tab,
        } satisfies AdIntelligencePublisherVerificationDetailsRequest;

        return queryOptions<AdIntelligenceRecordsPublisherVerificationDetailsResponse>({
            queryKey: [
                "WebsiteDisplayAds",
                "verificationDetails",
                advertiser,
                publisher,
                params.country,
                dateFrom,
                dateTo,
                position ?? null,
                tab,
                tabPage,
                pageSize,
            ],
            queryFn: () =>
                apiClient.AdIntelligenceMediaPartners.adIntelligenceAdvertiserPublisherVerificationDetailsCreate(body),
            enabled: enabled && Boolean(advertiser && publisher && params.country && dateFrom && dateTo),
        });
    });

    const results = useQueries({ queries });
    const all: DetailsQueryResult = results[0];
    const withScreenshots: DetailsQueryResult = results[1];
    const withoutScreenshots: DetailsQueryResult = results[2];

    const totalCounts: Record<AdIntelligenceEnumsScreenshotFilter, number> = {
        All: all.data?.totalCount ?? 0,
        HasScreenshot: withScreenshots.data?.totalCount ?? 0,
        NoScreenshot: withoutScreenshots.data?.totalCount ?? 0,
    };

    const records: Record<AdIntelligenceEnumsScreenshotFilter, AdIntelligenceRecordsDetailedVerification[]> = {
        All: all.data?.data ?? [],
        HasScreenshot: withScreenshots.data?.data ?? [],
        NoScreenshot: withoutScreenshots.data?.data ?? [],
    };

    return {
        records,
        totalCounts,
        isFetching: results.some((r) => r.isFetching),
        isError: results.some((r) => r.isError),
    };
};
